<script setup lang="ts">
import type { ProductContributorWithDetails } from "~/composables/useProductContributors"

const { contributors } = useProductContributors()

const isSingleContributor = computed(() => contributors.value && contributors.value.length === 1)
const shouldShowImage = computed(() => contributors.value && contributors.value.length <= 2)

function getInitials({ details }: ProductContributorWithDetails) {
  const initials = `${details?.firstname.charAt(0)}${details?.lastname.charAt(0)}`
  return initials.toUpperCase()
}

function getImageClass(i: number) {
  return {
    "z-10": i === 0,
    "translate-x-[65%]": i === 1,
  }
}
</script>

<template>
  <section aria-labelledby="contributors-heading" class="flex gap-5 font-national2 text-dark">
    <h2 id="contributors-heading" class="sr-only">
      Bidragsytere
    </h2>
    <div
      v-if="shouldShowImage"
      class="relative h-[60px] md:flex"
      :class="{ 'flex w-[60px]': isSingleContributor, hidden: !isSingleContributor, 'min-w-[100px]': contributors?.length === 2 }"
      aria-hidden="true"
    >
      <template v-for="(contributor, i) in contributors" :key="i">
        <img
          v-if="contributor.details?.image?.src"
          :src="contributor.details.image?.src"
          :alt="contributor.details.image?.alt"
          :class="getImageClass(i)"
          class="absolute size-[60px] rounded-full bg-lightest"
        />
        <!-- Show initials if no image -->
        <div
          v-else
          class="absolute flex size-[60px] items-center justify-center rounded-full border border-dark bg-dark text-platform-paper"
          :class="getImageClass(i)"
        >
          {{ getInitials(contributor) }}
        </div>
      </template>
    </div>
    <ul class="flex list-none flex-col justify-center p-0 text-base md:gap-2 md:text-lg/6">
      <li v-for="(contributor) in contributors" :key="contributor.locationId">
        <p
          v-if="contributor.details"
          class="font-extrabold"
          :class="{ 'flex flex-col': isSingleContributor }"
        >
          {{ contributor.details.firstname }} {{ contributor.details.lastname }}
          <span class="font-normal italic">
            {{ contributor.details.role }}
          </span>
        </p>
      </li>
    </ul>
  </section>
</template>
